// =============================================================================
// MeuDriver — ProgressReport (Batch T4)
// Componente A4-friendly para impressão do relatório de progresso do aluno.
// Visível apenas ao imprimir (@media print). Na tela normal fica oculto.
// =============================================================================

import type { ProgressReportData } from '@/hooks/useProgressReport'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface ProgressReportProps {
  data: ProgressReportData
}

export function ProgressReport({ data }: ProgressReportProps) {
  const {
    studentName,
    generatedAt,
    totalLessons,
    totalHours,
    milestones,
    badges,
    certificates,
    recentLessons,
  } = data

  const achievedMilestones = milestones.filter((m) => m.achieved)
  const pendingMilestones  = milestones.filter((m) => !m.achieved)

  return (
    <>
      {/* Print-only stylesheet injetado via <style> para garantir override */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #progress-report, #progress-report * { visibility: visible !important; }
          #progress-report {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
          @page { margin: 20mm; }
        }
      `}</style>

      <div
        id="progress-report"
        className="hidden print:block font-sans text-gray-900"
        aria-label="Relatório de progresso para impressão"
      >
        {/* ── Cabeçalho ── */}
        <div className="border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight">MeuDriver</h1>
              <p className="text-xs text-gray-500 uppercase tracking-widest">Relatório de Progresso</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">{studentName ?? 'Aluno'}</p>
              <p className="text-xs text-gray-500">
                Gerado em {generatedAt.toLocaleString('pt-BR', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </div>

        {/* ── KPIs ── */}
        <section className="mb-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">
            Resumo
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-black">{totalLessons}</p>
              <p className="text-xs text-gray-500 mt-0.5">Aulas concluídas</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-black">{totalHours}h</p>
              <p className="text-xs text-gray-500 mt-0.5">Horas de prática</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-black">{badges.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">Badges conquistados</p>
            </div>
          </div>
        </section>

        {/* ── Trilha de aprendizado ── */}
        {milestones.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">
              Trilha de Aprendizado
            </h2>
            <div className="space-y-2">
              {achievedMilestones.map((m) => (
                <div key={m.id} className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-2.5">
                  <span className="text-lg">{m.badge_icon}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{m.title}</p>
                    {m.achieved_at && (
                      <p className="text-xs text-gray-400">Concluído em {fmtDate(m.achieved_at)}</p>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    Concluido
                  </span>
                </div>
              ))}
              {pendingMilestones.slice(0, 3).map((m) => (
                <div key={m.id} className="flex items-center gap-3 border border-gray-100 rounded-lg px-4 py-2.5 opacity-50">
                  <span className="text-lg grayscale">{m.badge_icon}</span>
                  <p className="text-sm">{m.title}</p>
                  <span className="ml-auto text-xs text-gray-400">Pendente</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Badges ── */}
        {badges.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">
              Badges Conquistados
            </h2>
            <div className="grid grid-cols-4 gap-3">
              {badges.map((b) => (
                <div key={b.id} className="border border-gray-200 rounded-lg p-3 text-center">
                  <span className="text-2xl">{b.icon_emoji}</span>
                  <p className="text-xs font-medium mt-1 leading-tight">{b.name}</p>
                  {b.earned_at && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(b.earned_at)}</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Certificados ── */}
        {certificates.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">
              Certificados Emitidos
            </h2>
            <div className="space-y-2">
              {certificates.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-2.5">
                  <div>
                    <p className="text-sm font-semibold">{c.certificate_number}</p>
                    <p className="text-xs text-gray-500">
                      {c.total_lessons} aulas · {c.total_hours}h
                      {c.instructor_name ? ` · Instrutor: ${c.instructor_name}` : ''}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400">{fmtDate(c.issued_at)}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Histórico de aulas (últimas 10) ── */}
        {recentLessons.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">
              Histórico de Aulas (últimas {recentLessons.length})
            </h2>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left pb-2 font-semibold text-xs text-gray-500">Data/Hora</th>
                  <th className="text-left pb-2 font-semibold text-xs text-gray-500">Instrutor</th>
                  <th className="text-right pb-2 font-semibold text-xs text-gray-500">Duração</th>
                </tr>
              </thead>
              <tbody>
                {recentLessons.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100">
                    <td className="py-1.5 text-xs">{fmtDateTime(l.scheduled_at)}</td>
                    <td className="py-1.5 text-xs">{l.instructor_name ?? '—'}</td>
                    <td className="py-1.5 text-xs text-right">
                      {l.duration_minutes}min
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* ── Rodapé ── */}
        <div className="border-t border-gray-200 pt-4 mt-8">
          <p className="text-[10px] text-gray-400 text-center">
            Este relatório foi gerado automaticamente pelo MeuDriver em{' '}
            {generatedAt.toLocaleString('pt-BR')}. As informações são de caráter
            informativo e não substituem documentos oficiais emitidos por órgãos de
            trânsito.
          </p>
        </div>
      </div>
    </>
  )
}
